import type { PartDefinition } from "@rogue/rackbuilder"
import { MMS } from "../../modelPaths"
import type { MassStorageOptions } from "../../RackTypes"
import { addStorageUnitSide } from "../../utils/mms-utils/addon-utils"
import { getBaseShelfDataForHardware, type UnitShelfData, getHardwareCount } from "../../utils/mms-utils/hardware-counting-utils"
import { assignShelfPlacement } from "../../utils/mms-utils/mms-shelf-utils"
import { addVirtualMeshesToUnt } from "../../utils/mms-utils/mms-virtual-mesh-utils"
import type { BuyRequest } from "@/js/services/RogueTypes"

export class MonsterMassStorageBuilder {
  constructor(private config: MassStorageOptions, private buyRequest: BuyRequest) {}

  public build(): PartDefinition {
    let leftBase: PartDefinition = {
      mesh  : this.config.height === '100' ? `${MMS}#base` : `${MMS}#welded base`,
      slots : {},
    }

    const topBrandingPlate: PartDefinition = {
      mesh  : `${MMS}#branding plate`,
      slots : {},
    }

    let rightBase: PartDefinition = {
      mesh  : this.config.height === '100' ? `${MMS}#base` : `${MMS}#welded base`,
      slots : {},
    }

    const upright: PartDefinition = {
      mesh  : `${MMS}#upright ${this.config.height}`,
      slots : {},
    }

    const topCrossmember: PartDefinition = {
      mesh   : `${MMS}#${this.config.height === '100' ? 'top crossmember' : 'welded top crossmember'}`,
      slots  : {},
      offset : { z: -0.2 },
    }

    const frontWheel: PartDefinition = {
      mesh   : `${MMS}#wheel`,
      slots  : {},
      offset : { y: -0.5 },
    }

    const backWheel: PartDefinition = {
      mesh        : `${MMS}#wheel`,
      slots       : {},
      offset      : { y: -0.5 },
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
    }

    const rubberFeet: PartDefinition = {
      mesh   : `${MMS}#rubber feet`,
      slots  : {},
      anchor : 'feet',
    }

    // MARK: Base Rack Build
    rightBase = {
      mesh  : this.config.height === '100' ? `${MMS}#base` : `${MMS}#welded base`,
      slots : {
        front                                      : upright,
        back                                       : upright,
        [`crossmember_top_${this.config.height}h`] : topCrossmember,
        [`branding_${this.config.height}h`]        : topBrandingPlate,
        feet                                       : this.config.footSelection === 'feet' ? rubberFeet : [],
        front_wheel                                : this.config.footSelection === 'wheels' ? frontWheel : [],
        back_wheel                                 : this.config.footSelection === 'wheels' ? backWheel : [],
      },
    }

    leftBase = {
      mesh  : this.config.height === '100' ? `${MMS}#base` : `${MMS}#welded base`,
      slots : {
        front                                      : upright,
        back                                       : upright,
        [`crossmember_top_${this.config.height}h`] : topCrossmember,
        [`right_side_${this.config.width}h`]       : rightBase,
        feet                                       : this.config.footSelection === 'feet' ? rubberFeet : [],
        front_wheel                                : this.config.footSelection === 'wheels' ? frontWheel : [],
        back_wheel                                 : this.config.footSelection === 'wheels' ? backWheel : [],
      },
    }
    const leftNeighborIs100 = this.config.leftAddonChain?.[0]?.heightSelection === '100'
    const rightNeighborIs100 = this.config.rightAddonChain?.[0]?.heightSelection === '100'
    const isAdjacentTo100 = leftNeighborIs100 || rightNeighborIs100 || this.config.height === '100'

    // Base Shelf Selections
    assignShelfPlacement(leftBase, {
      shelfOneSelection   : this.config.shelfOneSelection,
      shelfTwoSelection   : this.config.shelfTwoSelection,
      shelfThreeSelection : this.config.shelfThreeSelection,
      shelfFourSelection  : this.config.shelfFourSelection,
      shelfFiveSelection  : this.config.shelfFiveSelection,
      heightSelection     : this.config.height,
    }, this.config, isAdjacentTo100)

    // Create right and left addon shelves
    if (this.config.leftAddonChain.length || this.config.rightAddonChain.length) {
      addStorageUnitSide(leftBase, this.config.leftAddonChain, 'left', this.config, frontWheel, backWheel, rubberFeet, this.buyRequest)
      addStorageUnitSide(rightBase, this.config.rightAddonChain, 'right', this.config, frontWheel, backWheel, rubberFeet, this.buyRequest)
    } else {
      const baseShelves = getBaseShelfDataForHardware(
        {
          shelfOneSelection   : this.config.shelfOneSelection,
          shelfTwoSelection   : this.config.shelfTwoSelection,
          shelfThreeSelection : this.config.shelfThreeSelection,
          shelfFourSelection  : this.config.shelfFourSelection,
          shelfFiveSelection  : this.config.shelfFiveSelection,
          heightSelection     : this.config.height,
        }
      )

      const baseUnitShelfData: UnitShelfData[] = [{
        unit             : 0,
        unitHeight       : this.config.height,
        shelves          : baseShelves,
        isEndUnit        : true, // Only one unit means it's both start and end
        adjacentToCorner : false, // Only one unit means no corners
      }]

      // Perform hardware count on base only
      const baseHardwareCount = getHardwareCount(baseUnitShelfData)

      // Add the hardwareCount to the BR
      if (this.buyRequest.children?.[0]?.metadata) {
        this.buyRequest.children[0].metadata.hardwareCount ??= {}
        this.buyRequest.children[0].metadata.hardwareCount = baseHardwareCount
      }
    }

    const virtualMeshCounter = { value: 0 }
    // Add virtual meshes to all shelf_two instances for selecting individual shelves
    addVirtualMeshesToUnt(leftBase, undefined, this.config, virtualMeshCounter)

    return leftBase
  }
}
